import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { DatePicker, Button, Segmented, Spin } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import DateRangeFilter from "./relatorios/components/DateRangeFilter";
import RankingDefeitosChart from "./relatorios/components/RankingDefeitosChart";
import RankingFornecedoresChart from "./relatorios/components/RankingFornecedoresChart";
import UnidadesPieChart from "./relatorios/components/UnidadesPieChart";
import TiposEmbalagensChart from "./relatorios/components/TiposEmbalagensChart";
import EvolucaoDiariaChart from "./relatorios/components/EvolucaoDiariaChart";
import EvolucaoFornecedoresStacked from "./relatorios/components/EvolucaoFornecedoresStacked";
import StatusEtapasRadar from "./relatorios/components/StatusEtapasRadar";
import AnaliseCompostaComposed from "./relatorios/components/AnaliseCompostaComposed";

import {
  GetRankingDefeitos,
  GetRankingFornecedores,
  GetRankingUnidades,
  GetRankingEmbalagens,
  GetEvolucaoSemanal,
  GetEvolucaoFornecedores,
  GetStatusPorEtapa,
  GetAnaliseFornecedorDefeitos,
  GetSeveridadePorEmbalagem,
  GetTempoMedioFechamento,
  GetRelatoriosPermitidos,
} from "../../../data/services";
import SeveridadePorEmbalagemRadar from "./relatorios/components/SeveridadePorEmbalagemChart";

import secureLocalStorage from "react-secure-storage";
import ChartWrapper from "./relatorios/components/ChartWrapper";

const colors = [
  "#d9534f",
  "#0275d8",
  "#5cb85c",
  "#f0ad4e",
  "#5bc0de",
  "#9b59b6",
  "#34495e",
];
const { RangePicker } = DatePicker;

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [allowedReports, setAllowedReports] = useState([]);
  const [login, setLogin] = useState(null);

  const [dateRange, setDateRange] = useState([null, null]);
  const [defeitosData, setDefeitosData] = useState([]);
  const [fornecedoresData, setFornecedoresData] = useState([]);
  const [unidadesData, setUnidadesData] = useState([]);
  const [embalagensData, setEmbalagensData] = useState([]);
  const [evolucaoData, setEvolucaoData] = useState([]);
  const [severidadeData, setSeveridadeData] = useState([]);
  const [evolucaoFornecedoresData, setEvolucaoFornecedoresData] = useState([]);
  const [statusEtapasData, setStatusEtapasData] = useState([]);
  const [analiseFornecedorDefeitosData, setAnaliseFornecedorDefeitosData] =
    useState([]);
  const [tempoFechamentoData, setTempoFechamentoData] = useState([]);

  const [preset, setPreset] = useState("7d");

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userLogin = secureLocalStorage.getItem("login");
        if (!userLogin) throw new Error("Usuário não autenticado");
        setLogin(userLogin);

        const response = await GetRelatoriosPermitidos(userLogin);

        setAllowedReports(response || []);
      } catch (err) {
        toast.error("Erro ao carregar permissões de acesso.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  const presets = useMemo(
    () => [
      { label: "7d", value: [dayjs().subtract(6, "day"), dayjs()] },
      { label: "14d", value: [dayjs().subtract(13, "day"), dayjs()] },
      { label: "30d", value: [dayjs().subtract(29, "day"), dayjs()] },
      { label: "YTD", value: [dayjs().startOf("year"), dayjs()] },
    ],
    []
  );

  const applyPreset = (key) => {
    const found = presets.find((p) => p.label === key);
    if (found) {
      setDateRange(found.value);
      setPreset(key);
    }
  };

  const disabledDate = (current) => current && current > dayjs().endOf("day");

  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      setPreset(null);
    }
  };

  const handleCalendarChange = (dates) => {
    if (dates && dates[0] && !dates[1]) return;
  };

  const periodLabel = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return "—";
    return `${dateRange[0].format("DD/MM/YYYY")} • ${dateRange[1].format(
      "DD/MM/YYYY"
    )}`;
  }, [dateRange]);

  const diasSelecionados = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return 0;
    return dateRange[1].diff(dateRange[0], "day") + 1;
  }, [dateRange]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      const inicio = dateRange[0].format("YYYY-MM-DD");
      const fim = dateRange[1].format("YYYY-MM-DD");

      Promise.all([
        GetRankingDefeitos(inicio, fim),
        GetRankingFornecedores(inicio, fim),
        GetRankingUnidades(inicio, fim),
        GetRankingEmbalagens(inicio, fim),
        GetEvolucaoSemanal(inicio, fim),
        GetSeveridadePorEmbalagem(inicio, fim),
        GetEvolucaoFornecedores(inicio, fim),
        GetStatusPorEtapa(inicio, fim),
        GetAnaliseFornecedorDefeitos(inicio, fim),
        GetTempoMedioFechamento(inicio, fim),
      ]).then(
        ([
          defeitos,
          fornecedores,
          unidades,
          embalagens,
          evolucao,
          severidade,
          evolucaoFornecedores,
          statusEtapas,
          analiseFornecedorDefeitos,
          tempoFechamento,
        ]) => {
          setDefeitosData(defeitos);
          setFornecedoresData(fornecedores);
          setUnidadesData(unidades);
          setEmbalagensData(embalagens);
          setEvolucaoData(evolucao);
          setSeveridadeData(severidade);
          setEvolucaoFornecedoresData(evolucaoFornecedores);
          setStatusEtapasData(statusEtapas);
          setAnaliseFornecedorDefeitosData(analiseFornecedorDefeitos);
          setTempoFechamentoData(tempoFechamento);
        }
      );
    }
  }, [dateRange]);

  // ==============================
  // SE ESTIVER CARREGANDO
  // ==============================
  if (loading) {
    return (
      <Container className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Carregando permissões e relatórios..." />
      </Container>
    );
  }

  // ==============================
  // RENDERIZAÇÃO CONDICIONAL DOS GRÁFICOS
  // ==============================
  const canSee = (reportName) => allowedReports.includes(reportName);

  return (
    <Container>
      <Header>
        <div>
          <h1>📊 Painel de Indicadores de NCEs</h1>
          <span className="periodo">Período: {periodLabel}</span>
        </div>

        <FilterBar>
          <Segmented
            value={preset || undefined}
            onChange={applyPreset}
            options={presets.map((p) => p.label)}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={dateRange}
            onChange={handleRangeChange}
            onCalendarChange={handleCalendarChange}
            allowClear={false}
            disabledDate={disabledDate}
            style={{ width: 260 }}
          />
        </FilterBar>
      </Header>

      <Grid>
        {/* KPI cards sempre visíveis */}
        {canSee("DateRangeFilter.jsx") && (
          <>
            <Card className="kpi">
              <KPI>
                <h4>Total de NCEs</h4>
                <span className="valor">
                  {unidadesData?.reduce(
                    (acc, item) => acc + (item.value || 0),
                    0
                  )}
                </span>
                <small>
                  {diasSelecionados > 1 ? `${diasSelecionados} dias` : "1 dia"}
                </small>
              </KPI>
            </Card>

            <Card className="kpi">
              <KPI>
                <h4>Fornecedores Envolvidos</h4>
                <span className="valor">{fornecedoresData?.length || 0}</span>
                <small>Período atual</small>
              </KPI>
            </Card>

            <Card className="kpi">
              <KPI>
                <h4>Tempo Médio de Fechamento</h4>
                <span className="valor">
                  {tempoFechamentoData.find((t) => t.status === "CONCLUIDA")
                    ?.tempo_medio_dias || "-"}
                </span>
              </KPI>
            </Card>
          </>
        )}
        {/* Relatórios condicionais */}
        {canSee("UnidadesPieChart.jsx") && (
          <Card className="small">
            <Title>Unidades que abriram NCE</Title>
            <ChartWrapper data={unidadesData} height={320}>
              <UnidadesPieChart
                data={unidadesData}
                colors={colors}
                height={240}
              />
            </ChartWrapper>
          </Card>
        )}

        {canSee("StatusEtapasRadar.jsx") && (
          <Card className="small">
            <Title>Distribuição das NCEs por Etapa</Title>
            <ChartWrapper data={statusEtapasData} height={320}>
              <StatusEtapasRadar data={statusEtapasData} height={240} />
            </ChartWrapper>
          </Card>
        )}

        {canSee("RankingDefeitosChart.jsx") && (
          <Card className="half">
            <Title>Ranking de Defeitos</Title>
            {/* <RankingDefeitosChart data={defeitosData} height={320} /> */}
            <ChartWrapper data={defeitosData} height={320}>
              <RankingDefeitosChart data={defeitosData} height={320} />
            </ChartWrapper>
          </Card>
        )}

        {canSee("RankingFornecedoresChart.jsx") && (
          <Card className="half">
            <Title>Ranking de Fornecedores</Title>
            <ChartWrapper data={fornecedoresData} height={320}>
              <RankingFornecedoresChart data={fornecedoresData} height={320} />
            </ChartWrapper>
          </Card>
        )}

        {canSee("TiposEmbalagensChart.jsx") && (
          <Card className="half">
            <Title>Tipos de Embalagens</Title>
            <ChartWrapper data={embalagensData} height={320}>
              <TiposEmbalagensChart data={embalagensData} height={320} />
            </ChartWrapper>
          </Card>
        )}

        {canSee("EvolucaoDiariaChart.jsx") && (
          <Card className="half">
            <Title>Evolução Diária de NCEs</Title>
            <ChartWrapper data={evolucaoData} height={320}>
              <EvolucaoDiariaChart data={evolucaoData} height={320} />
            </ChartWrapper>
          </Card>
        )}

        {canSee("EvolucaoFornecedoresStacked.jsx") && (
          <Card className="half">
            <Title>Evolução Diária dos Principais Fornecedores</Title>
            <ChartWrapper data={evolucaoFornecedoresData} height={360}>
              <EvolucaoFornecedoresStacked
                data={evolucaoFornecedoresData}
                colors={colors}
                height={360}
                stacked
              />
            </ChartWrapper>
          </Card>
        )}

        {canSee("SeveridadePorEmbalagemChart.jsx") && (
          <Card className="half">
            <Title>Severidade por Embalagem</Title>
            <ChartWrapper data={severidadeData} height={320}>
              <SeveridadePorEmbalagemRadar data={severidadeData} height={320} />
            </ChartWrapper>
          </Card>
        )}

        {canSee("AnaliseCompostaComposed.jsx") && (
          <Card className="full">
            <Title>Análise Composta — Fornecedores × Defeitos × Unidades</Title>
            <ChartWrapper data={analiseFornecedorDefeitosData} height={380}>
              <AnaliseCompostaComposed
                data={analiseFornecedorDefeitosData}
                height={380}
              />
            </ChartWrapper>
          </Card>
        )}
      </Grid>
    </Container>
  );
};

export default AdminDashboard;

/* ==================== styles ==================== */

const Container = styled.div`
  padding: 24px;
  background: #f7f8fb;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h1 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 4px;
  }

  .periodo {
    color: #6b7280;
    font-size: 12px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .ant-segmented {
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(16, 24, 40, 0.06);
    border-radius: 10px;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(12, minmax(0, 1fr));

  @media (max-width: 1200px) {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 12px rgba(16, 24, 40, 0.06);
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-height: 140px;

  &.kpi {
    grid-column: span 4;
    min-height: 120px;
  }

  &.small {
    grid-column: span 6;
    min-height: 200px;
  }

  &.half {
    grid-column: span 6;
    min-height: 280px;
  }

  &.full {
    grid-column: 1 / -1;
    min-height: 360px;
  }

  @media (max-width: 1200px) {
    &.kpi {
      grid-column: span 4;
    }
    &.small {
      grid-column: span 8;
    }
    &.half {
      grid-column: span 8;
    }
  }

  @media (max-width: 900px) {
    &.kpi,
    &.small,
    &.half,
    &.full {
      grid-column: 1 / -1;
    }
  }
`;

const Title = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 4px 0 12px;
`;

const KPI = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 6px;

  h4 {
    margin: 0;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
  }

  .valor {
    font-size: 32px;
    font-weight: 800;
    color: #2563eb;
    line-height: 1;
  }

  small {
    color: #9ca3af;
    font-size: 12px;
  }
`;
